import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Navigate, useNavigate } from "react-router-dom";

import { useLanguage } from "@/context/LanguageContext";
import { CardOfTheDay } from "@/components/CardOfTheDay";
import { clearConsultant } from "@/lib/dailyCard";

const FIRSTNAME_KEY = "arcana.firstName.v1";

const DailyPage = () => {
  const { t } = useLanguage();
  const nav = useNavigate();
  const [firstName, setFirstName] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    setFirstName(window.localStorage.getItem(FIRSTNAME_KEY) || "");
  }, []);

  if (firstName === null) {
    return (
      <div className="min-h-[calc(100vh-72px)] flex items-center justify-center">
        <div className="pulse-square" />
      </div>
    );
  }
  if (!firstName) return <Navigate to="/" replace />;

  const handleFinish = () => {
    clearConsultant();
    nav("/", { replace: true });
  };

  return (
    <main
      data-testid="daily-page"
      className="relative min-h-[calc(100vh-72px)] max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-14"
    >
      <section className="mb-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div className="rune text-sm tracking-[0.5em] uppercase text-gold/70 mb-4">✦ ARCANA MYSTIQUE ✦</div>
          <h1
            data-testid="daily-greeting"
            className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.1] text-ivory tracking-tight"
          >
            <span className="block uppercase tracking-[0.08em]">{t.home.dailyTitle}</span>
            <span className="block italic shimmer-gold">{firstName}</span>
          </h1>
        </motion.div>
      </section>

      <div className="gold-hairline mb-10" />

      <CardOfTheDay
        firstName={firstName}
        actions={
          <div className="mt-8 flex flex-col sm:flex-row gap-4" data-testid="daily-actions">
            <button
              type="button"
              onClick={() => nav("/welcome")}
              data-testid="daily-go-reading-btn"
              className="group inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-gold text-oxblood hover:bg-gold-soft transition-colors duration-300 text-[0.75rem] tracking-[0.4em] uppercase font-semibold"
            >
              {t.daily.goReading}
              <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </button>
            <button
              type="button"
              onClick={handleFinish}
              data-testid="daily-finish-btn"
              className="inline-flex items-center justify-center px-8 py-4 rounded-full border border-gold/40 text-ivory/80 hover:border-gold hover:text-gold transition-colors duration-300 text-[0.75rem] tracking-[0.4em] uppercase"
            >
              {t.daily.finish}
            </button>
          </div>
        }
      />

      <div className="mt-10 pt-6 border-t border-gold/10 text-center">
        <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ivory/40">{t.footer}</p>
      </div>
    </main>
  );
};

export default DailyPage;
